import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Button from '../../components/ui/Button';
import LoadingMark from '../../components/ui/LoadingMark';
import Icon from '../../components/AppIcon';
import PageHeader from '../../components/PageHeader';
import NotificationItem from './components/NotificationItem';
import NotificationFilters from './components/NotificationFilters';
import NotificationEmptyState from './components/NotificationEmptyState';
import NotificationBulkActions from './components/NotificationBulkActions';
import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';

import { useNotifications } from '../../context/NotificationContext';
import { isLicenseSharingInviteNotification } from '../../utils/notificationTypes';

const NotificationsPage = () => {
  const navigate = useNavigate();

  const {
    notifications,
    loading,
    loadNotifications,
    markNotificationAsRead,
    deleteSingleNotification,
    clearAllNotifications
  } = useNotifications();

  const [activeFilter, setActiveFilter] = useState('all');
  const [clearMessage, setClearMessage] = useState('');

  useEffect(() => {
    loadNotifications();
  }, []);

  const filteredNotifications =
    activeFilter === 'unread'
      ? notifications.filter(n => !n.isRead)
      : notifications;

  const notificationCounts = {
    all: notifications.length,
    unread: notifications.filter(n => !n.isRead).length
  };

  const handleAction = (notification) => {
    if (!notification.isRead) {
      markNotificationAsRead(notification);
    }

    if (notification.actionButton?.url) {
      navigate(notification.actionButton.url);
    }
  };

  const handleMarkAllAsRead = async () => {
    for (const n of notifications) {
      if (!n.isRead) {
        await markNotificationAsRead(n);
      }
    }
  };

  const handleClearAll = async () => {
    const result = await clearAllNotifications();
    const deleted = result?.deleted_count ?? 0;
    const skipped = result?.skipped_protected_count ?? 0;
    let message = `Total de ${deleted} notificações excluídas`;
    if (skipped > 0) {
      message += `. ${skipped} convite(s) de licença permanecem até aceitar ou recusar.`;
    }
    setClearMessage(message);
  };

  const pendingLicenseInvites = notifications.filter(isLicenseSharingInviteNotification)
    .length;
  const deletableNotificationCount = notifications.length - pendingLicenseInvites;

  const showToolbar = !loading && notificationCounts.all > 0;

  return (
    <>
      <Helmet>
        <title>Notificações - neo-modelos</title>
      </Helmet>

      <div className={PAGE_CONTENT_CLASS}>
        <PageHeader
          title="Notificações"
          subtitle="Acompanhe atualizações sobre documentos, assinaturas e licenças"
          icon="Bell"
          action={
            <Button
              variant="outline"
              onClick={() => navigate('/account-management')}
              className="w-full sm:w-auto gap-2"
            >
              <Icon name="UserCircle" size={18} />
              Minha Conta
            </Button>
          }
        />

        {clearMessage && (
          <div
            className="mb-6 flex items-start gap-3 rounded-xl border border-success/30 bg-success/5 px-4 py-3 text-sm text-success"
            role="status"
          >
            <Icon name="CheckCircle2" size={18} className="shrink-0 mt-0.5" />
            <p>{clearMessage}</p>
          </div>
        )}

        {showToolbar && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5 mb-6 shadow-elevation-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <NotificationFilters
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                notificationCounts={notificationCounts}
              />
              <NotificationBulkActions
                totalUnread={notificationCounts.unread}
                totalNotifications={notificationCounts.all}
                deletableCount={deletableNotificationCount}
                pendingLicenseInvites={pendingLicenseInvites}
                onMarkAllAsRead={handleMarkAllAsRead}
                onClearAll={handleClearAll}
              />
            </div>
          </div>
        )}

        {!showToolbar && !loading && (
          <div className="mb-6">
            <NotificationFilters
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              notificationCounts={notificationCounts}
            />
          </div>
        )}

        {loading ? (
          <div className="bg-card border border-border rounded-xl p-10 md:p-14 flex justify-center shadow-elevation-sm">
            <LoadingMark message="Carregando notificações…" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <NotificationEmptyState
            filterType={activeFilter}
            onClearFilters={() => setActiveFilter('all')}
          />
        ) : (
          <div className="space-y-3 md:space-y-4">
            {filteredNotifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={() => markNotificationAsRead(notification)}
                onAction={() => handleAction(notification)}
                onDelete={(options) => deleteSingleNotification(notification, options)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationsPage;
